"use client";

import React from "react";
import { Button, Tooltip } from "@heroui/react";
import { Icon } from "@iconify/react";
import { useTranslation } from "react-i18next";

import { REGENERATION_STRATEGIES } from "@/constants/regeneration";

export interface AttemptNavigationProps {
  currentAttempt: number;
  totalAttempts: number;
  onNavigate: (attemptNumber: number) => void;
  isLoading?: boolean;
  show?: boolean;
}

const AttemptNavigation = React.forwardRef<HTMLDivElement, AttemptNavigationProps>(
  ({ currentAttempt, totalAttempts, onNavigate, isLoading = false, show = true }, ref) => {
    const { t } = useTranslation();
    const [touchStart, setTouchStart] = React.useState<{ x: number; y: number } | null>(null);

    const getAttemptType = (attemptNumber: number): string | null => {
      const strategyIndex = (attemptNumber - 1) % REGENERATION_STRATEGIES.length;

      // Only show tooltip for strategies we have
      if (attemptNumber > REGENERATION_STRATEGIES.length) return null;

      const strategyKey = REGENERATION_STRATEGIES[strategyIndex];

      return t(`attempt.type.${strategyKey}`);
    };

    if (!show) {
      return null;
    }

    const canGoPrevious = currentAttempt > 1 && !isLoading;
    const canGoNext = currentAttempt < totalAttempts && !isLoading;

    const handlePrevious = () => {
      if (canGoPrevious) {
        onNavigate(currentAttempt - 1);
      }
    };

    const handleNext = () => {
      if (canGoNext) {
        onNavigate(currentAttempt + 1);
      }
    };

    // Touch gesture handlers
    const handleTouchStart = (e: React.TouchEvent) => {
      const touch = e.touches[0];

      setTouchStart({ x: touch.clientX, y: touch.clientY });
    };

    const handleTouchEnd = (e: React.TouchEvent) => {
      if (!touchStart) return;

      const touch = e.changedTouches[0];
      const deltaX = touch.clientX - touchStart.x;
      const deltaY = Math.abs(touch.clientY - touchStart.y);

      // Only trigger if horizontal swipe is more prominent than vertical
      const minSwipeDistance = 50;

      if (Math.abs(deltaX) > minSwipeDistance && deltaY < Math.abs(deltaX) / 2) {
        if (deltaX > 0 && canGoPrevious) {
          // Swipe right -> Previous attempt
          handlePrevious();
        } else if (deltaX < 0 && canGoNext) {
          // Swipe left -> Next attempt
          handleNext();
        }
      }

      setTouchStart(null);
    };

    const handleTouchCancel = () => {
      setTouchStart(null);
    };

    return (
      <div
        ref={ref}
        aria-label={t("Attempt navigation")}
        className="flex items-center gap-1 ml-2"
        role="navigation"
        onTouchCancel={handleTouchCancel}
        onTouchEnd={handleTouchEnd}
        onTouchStart={handleTouchStart}
      >
        <Button
          isIconOnly
          aria-label={t("Previous attempt")}
          className="min-w-6 w-6 h-6"
          isDisabled={!canGoPrevious}
          radius="full"
          size="sm"
          variant="light"
          onPress={handlePrevious}
        >
          <Icon className="text-sm text-default-600" icon="solar:alt-arrow-left-linear" />
        </Button>

        <Tooltip
          content={getAttemptType(currentAttempt)}
          isDisabled={!getAttemptType(currentAttempt)}
          placement="top"
        >
          <span
            aria-label={t("Current attempt {{current}} of {{total}}", {
              current: currentAttempt,
              total: totalAttempts,
            })}
            aria-live="polite"
            className="text-xs text-default-600 font-medium px-1 min-w-[2rem] text-center cursor-help"
          >
            {currentAttempt}/{totalAttempts}
          </span>
        </Tooltip>

        <Button
          isIconOnly
          aria-label={t("Next attempt")}
          className="min-w-6 w-6 h-6"
          isDisabled={!canGoNext}
          radius="full"
          size="sm"
          variant="light"
          onPress={handleNext}
        >
          <Icon className="text-sm text-default-600" icon="solar:alt-arrow-right-linear" />
        </Button>
      </div>
    );
  },
);

AttemptNavigation.displayName = "AttemptNavigation";

export { AttemptNavigation };
